import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Page,
  Button,
  Sheet,
  Range,
  Dialog,
  DialogButton,
  Toast,
  List,
  ListItem,
} from 'konsta/react'
import {
  ArrowLeft,
  Menu,
  Coins,
  History,
  LogOut,
  DoorOpen,
  Volume2,
  VolumeX,
  Eye,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { motion, AnimatePresence } from 'framer-motion'

// ============================================================
// Types
// ============================================================

interface Player {
  seat: number
  name: string | null
  stack?: number
  bet?: number
  isDealer?: boolean
  isActive?: boolean
  cards?: string[] | null
  status?: 'active' | 'folded' | 'allin' | 'sitting-out'
}

interface ChatMessage {
  id: string
  seat: number
  text: string
  timestamp: number
}

// ============================================================
// Mock Data
// ============================================================

const mockPlayers: Player[] = [
  { seat: 0, name: '王大明', stack: 125000, bet: 1000, isDealer: false, isActive: false, cards: ['A♠', 'K♠'], status: 'active' },
  { seat: 1, name: 'David C.', stack: 89000, bet: 2000, isDealer: true, isActive: false, cards: null, status: 'active' },
  { seat: 2, name: null },
  { seat: 3, name: 'Sarah L.', stack: 156000, bet: 0, isDealer: false, isActive: false, cards: null, status: 'folded' },
  { seat: 4, name: 'Mike W.', stack: 67000, bet: 5000, isDealer: false, isActive: true, cards: null, status: 'active' },
  { seat: 5, name: null },
  { seat: 6, name: '小美', stack: 203000, bet: 5000, isDealer: false, isActive: false, cards: null, status: 'active' },
  { seat: 7, name: 'Alex K.', stack: 45000, bet: 0, isDealer: false, isActive: false, cards: null, status: 'allin' },
  { seat: 8, name: null },
]

const communityCards = ['Q♠', 'J♥', '10♦', '8♣', '']

const roomInfo = {
  name: '新手桌 #1',
  blinds: '$1/$2',
  type: 'NLH',
  mainPot: 1400000,
  sidePot: 500000,
}

// Seat positions (absolute, for landscape layout)
const seatPositions = [
  { top: '78%', left: '50%' },   // 0: bottom center (hero)
  { top: '65%', left: '15%' },   // 1: bottom left
  { top: '35%', left: '5%' },    // 2: mid left
  { top: '8%', left: '15%' },    // 3: top left
  { top: '5%', left: '38%' },    // 4: top center-left
  { top: '5%', left: '62%' },    // 5: top center-right
  { top: '8%', left: '85%' },    // 6: top right
  { top: '35%', left: '95%' },   // 7: mid right
  { top: '65%', left: '85%' },   // 8: bottom right
]

// Bet chip positions (offset from seat toward center)
const betPositions = [
  { top: '65%', left: '50%' },   // 0
  { top: '57%', left: '22%' },   // 1
  { top: '40%', left: '14%' },   // 2
  { top: '20%', left: '22%' },   // 3
  { top: '18%', left: '38%' },   // 4
  { top: '18%', left: '62%' },   // 5
  { top: '20%', left: '78%' },   // 6
  { top: '40%', left: '86%' },   // 7
  { top: '57%', left: '78%' },   // 8
]

// Avatar colors by seat index
const avatarColors = [
  '#007AFF', '#FF9500', '#34C759', '#AF52DE',
  '#FF3B30', '#5AC8FA', '#FF2D55', '#FFCC00', '#8E8E93',
]

const initialChatMessages: ChatMessage[] = [
  { id: '1', seat: 1, text: 'Nice hand! 👍', timestamp: Date.now() - 3000 },
  { id: '2', seat: 6, text: '運氣好啊', timestamp: Date.now() - 1500 },
]

// ============================================================
// Card Display Component
// ============================================================

function CardDisplay({ card, faceDown, small }: { card: string; faceDown?: boolean; small?: boolean }) {
  const w = small ? 'w-7 h-10' : 'w-10 h-14'

  if (faceDown || !card) {
    return (
      <div className={cn(
        w,
        'rounded-lg bg-blue-700 border border-blue-500 flex items-center justify-center',
        'shadow-md',
      )}>
        <div className="w-[60%] h-[70%] rounded border border-blue-400/40 bg-blue-600" />
      </div>
    )
  }

  const isRed = card.includes('♥') || card.includes('♦')
  const fontSize = small ? 'text-[10px]' : 'text-sm'

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, rotateZ: -5, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, rotateZ: 0, scale: 1 }}
      transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
      className={cn(
        w,
        'rounded-lg bg-white border border-gray-200 flex items-center justify-center font-bold shadow-md',
        fontSize,
        isRed ? 'text-red-500' : 'text-gray-900',
      )}
    >
      {card}
    </motion.div>
  )
}

// ============================================================
// Player Seat Component
// ============================================================

function PlayerSeat({
  player,
  position,
  betPosition,
  color,
  isHero,
  onSitDown,
  timerProgress,
}: {
  player: Player
  position: { top: string; left: string }
  betPosition: { top: string; left: string }
  color: string
  isHero: boolean
  onSitDown: (seat: number) => void
  timerProgress: number
}) {
  const isEmpty = !player.name
  const isFolded = player.status === 'folded'
  const isAllIn = player.status === 'allin'
  const isActive = player.isActive

  return (
    <>
      {/* Bet chips near seat */}
      {player.bet && player.bet > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="absolute z-10 flex items-center gap-1"
          style={{
            top: betPosition.top,
            left: betPosition.left,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div className="w-4 h-4 rounded-full bg-yellow-400 border-2 border-yellow-500 shadow-sm" />
          <span className="text-[10px] font-bold text-white drop-shadow-md">
            {formatCurrency(player.bet)}
          </span>
        </motion.div>
      )}

      {/* Player seat */}
      <div
        className="absolute z-20 flex flex-col items-center"
        style={{
          top: position.top,
          left: position.left,
          transform: 'translate(-50%, -50%)',
        }}
      >
        {/* Empty seat */}
        {isEmpty ? (
          <button
            onClick={() => onSitDown(player.seat)}
            className="flex flex-col items-center gap-0.5 group"
          >
            <div className="w-11 h-11 rounded-full border-2 border-dashed border-white/30 flex items-center justify-center bg-black/20 group-active:bg-black/40 transition-colors">
              <span className="text-white/50 text-[10px] font-medium">空位</span>
            </div>
            <span className="text-[9px] text-[#34C759] font-medium opacity-80">入座</span>
          </button>
        ) : (
          <div className={cn('flex flex-col items-center gap-0.5', isFolded && 'opacity-40')}>
            {/* Active player timer ring */}
            {isActive && (
              <svg
                className="absolute -top-1 -left-1 w-[52px] h-[52px]"
                viewBox="0 0 52 52"
                style={{ transform: 'rotate(-90deg)' }}
              >
                <circle
                  cx="26"
                  cy="26"
                  r="23"
                  fill="none"
                  stroke="rgba(0,122,255,0.15)"
                  strokeWidth="3"
                />
                <circle
                  cx="26"
                  cy="26"
                  r="23"
                  fill="none"
                  stroke="#007AFF"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 23}`}
                  strokeDashoffset={`${2 * Math.PI * 23 * (1 - timerProgress)}`}
                  className="transition-all duration-1000 ease-linear"
                />
              </svg>
            )}

            {/* Avatar */}
            <div
              className={cn(
                'w-11 h-11 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg',
                isActive && 'ring-2 ring-[#007AFF] ring-offset-1 ring-offset-transparent',
                isAllIn && 'ring-2 ring-[#FF3B30]',
              )}
              style={{ backgroundColor: color }}
            >
              {player.name!.charAt(0)}
            </div>

            {/* Name + stack plate */}
            <div className="bg-black/70 rounded-md px-2 py-0.5 min-w-[60px] text-center backdrop-blur-sm">
              <div className="text-white text-[10px] font-medium truncate max-w-[70px]">
                {player.name}
              </div>
              <div className="text-yellow-400 text-[9px] font-semibold">
                {formatCurrency(player.stack || 0)}
              </div>
            </div>

            {/* Status badges */}
            {isAllIn && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="bg-[#FF3B30] text-white text-[8px] font-bold px-1.5 py-0 rounded-full uppercase tracking-wide"
              >
                ALL IN
              </motion.div>
            )}
            {isFolded && (
              <div className="text-white/40 text-[8px] font-medium uppercase tracking-wide">
                FOLD
              </div>
            )}

            {/* Dealer button */}
            {player.isDealer && (
              <div className="absolute -top-1 -right-2 w-5 h-5 rounded-full bg-white text-black text-[9px] font-black flex items-center justify-center shadow-md border border-gray-300">
                D
              </div>
            )}

            {/* Face-down cards for non-hero occupied seats */}
            {!isHero && !isEmpty && !isFolded && player.cards === null && (
              <div className="flex gap-0.5 mt-0.5">
                <CardDisplay card="" faceDown small />
                <CardDisplay card="" faceDown small />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

// ============================================================
// Chat Bubble Component
// ============================================================

function ChatBubble({ message, seatPos }: { message: ChatMessage; seatPos: { top: string; left: string } }) {
  // Offset chat bubble slightly above and to the right of the seat
  const topNum = parseFloat(seatPos.top) - 12
  const leftNum = parseFloat(seatPos.left) + 6

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.8, y: -5 }}
      transition={{ duration: 0.25 }}
      className="absolute z-30 pointer-events-none"
      style={{
        top: `${Math.max(2, topNum)}%`,
        left: `${Math.min(90, leftNum)}%`,
        transform: 'translate(-50%, -100%)',
      }}
    >
      <div className="bg-white/90 backdrop-blur-sm text-gray-900 text-[10px] font-medium px-2.5 py-1.5 rounded-xl rounded-bl-sm shadow-lg max-w-[120px] whitespace-nowrap">
        {message.text}
      </div>
    </motion.div>
  )
}

// ============================================================
// Main Component
// ============================================================

export function GameTableScreen() {
  const navigate = useNavigate()
  const { roomId: _roomId } = useParams()
  const user = useAuthStore((s) => s.user)

  // --- State ---
  const [showMenu, setShowMenu] = useState(false)
  const [showBuyIn, setShowBuyIn] = useState(false)
  const [showLeaveDialog, setShowLeaveDialog] = useState(false)
  const [raiseAmount, setRaiseAmount] = useState(10000) // $100 in cents
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(initialChatMessages)
  const [toastMessage, setToastMessage] = useState('')
  const [toastOpen, setToastOpen] = useState(false)
  const [buyInAmount, setBuyInAmount] = useState(30000) // $300 default
  const [isMuted, setIsMuted] = useState(false)
  const [isHeroTurn, setIsHeroTurn] = useState(false)
  const [timerProgress, setTimerProgress] = useState(0.75)
  const [players, setPlayers] = useState<Player[]>(mockPlayers)
  const [showHeroCards, setShowHeroCards] = useState(true)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Room config for buy-in
  const minBuyIn = 10000  // $100
  const maxBuyIn = 50000  // $500
  const currentBet = 5000 // current bet to call: $50
  const pot = roomInfo.mainPot + roomInfo.sidePot
  const heroPlayer = players[0]
  const heroStack = heroPlayer.stack || 0

  // --- Timer simulation for active player ---
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimerProgress((prev) => {
        const next = prev - 0.01
        if (next <= 0) return 0.75 // reset
        return next
      })
    }, 300)

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  // --- Chat auto-dismiss ---
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now()
      setChatMessages((prev) => prev.filter((m) => now - m.timestamp < 5000))
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  // --- Simulate hero turn toggle (demo) ---
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsHeroTurn(true)
      showToast('輪到你了!')
    }, 3000)
    return () => clearTimeout(timer)
  }, [])

  // --- Helpers ---
  const showToast = useCallback((msg: string) => {
    setToastMessage(msg)
    setToastOpen(true)
  }, [])

  const handleFold = useCallback(() => {
    setIsHeroTurn(false)
    setPlayers((prev) =>
      prev.map((p) => (p.seat === 0 ? { ...p, status: 'folded' as const, bet: 0 } : p))
    )
    showToast('你已棄牌')
  }, [showToast])

  const handleCall = useCallback(() => {
    setIsHeroTurn(false)
    setPlayers((prev) =>
      prev.map((p) => (p.seat === 0 ? { ...p, bet: currentBet, stack: (p.stack || 0) - currentBet + (p.bet || 0) } : p))
    )
    showToast(`跟注 ${formatCurrency(currentBet)}`)
  }, [currentBet, showToast])

  const handleRaise = useCallback(() => {
    setIsHeroTurn(false)
    setPlayers((prev) =>
      prev.map((p) => (p.seat === 0 ? { ...p, bet: raiseAmount, stack: (p.stack || 0) - raiseAmount + (p.bet || 0) } : p))
    )
    showToast(`加注到 ${formatCurrency(raiseAmount)}`)
  }, [raiseAmount, showToast])

  const handleAllIn = useCallback(() => {
    setIsHeroTurn(false)
    const allInAmount = heroStack + (heroPlayer.bet || 0)
    setPlayers((prev) =>
      prev.map((p) => (p.seat === 0 ? { ...p, bet: allInAmount, stack: 0, status: 'allin' as const } : p))
    )
    showToast(`全押! ${formatCurrency(allInAmount)}`)
  }, [heroStack, heroPlayer.bet, showToast])

  const handleSitDown = useCallback((_seat: number) => {
    setShowBuyIn(true)
  }, [])

  const handleConfirmBuyIn = useCallback(() => {
    setShowBuyIn(false)
    showToast(`買入 ${formatCurrency(buyInAmount)} 成功`)
  }, [buyInAmount, showToast])

  const handleLeaveRoom = useCallback(() => {
    setShowLeaveDialog(false)
    navigate('/rooms')
  }, [navigate])

  // Quick raise helpers
  const halfPot = Math.max(currentBet * 2, Math.floor(pot / 2))
  const threeFourthPot = Math.max(currentBet * 2, Math.floor((pot * 3) / 4))
  const fullPot = Math.max(currentBet * 2, pot)
  const minRaise = currentBet * 2

  return (
    <Page className="!bg-black no-select">
      {/* ========== TABLE FELT ========== */}
      <div className="absolute inset-0 table-felt">
        {/* Table oval border */}
        <div
          className="absolute"
          style={{
            top: '12%',
            left: '8%',
            right: '8%',
            bottom: '22%',
            border: '3px solid rgba(255,255,255,0.08)',
            borderRadius: '50%',
          }}
        />

        {/* ========== NAVBAR (overlay) ========== */}
        <div className="absolute top-0 left-0 right-0 z-50">
          <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-b from-black/60 to-transparent">
            <button
              onClick={() => setShowLeaveDialog(true)}
              className="w-8 h-8 rounded-full bg-black/40 flex items-center justify-center active:bg-black/60"
            >
              <ArrowLeft className="w-4 h-4 text-white" />
            </button>

            <div className="text-center">
              <div className="text-white text-[13px] font-semibold">{roomInfo.name}</div>
              <div className="text-white/50 text-[10px]">{roomInfo.blinds} {roomInfo.type}</div>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setIsMuted(!isMuted)}
                className="w-8 h-8 rounded-full bg-black/40 flex items-center justify-center active:bg-black/60"
              >
                {isMuted ? (
                  <VolumeX className="w-4 h-4 text-white/60" />
                ) : (
                  <Volume2 className="w-4 h-4 text-white" />
                )}
              </button>
              <button
                onClick={() => setShowMenu(true)}
                className="w-8 h-8 rounded-full bg-black/40 flex items-center justify-center active:bg-black/60"
              >
                <Menu className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* ========== POT DISPLAY ========== */}
        <div
          className="absolute z-10 flex flex-col items-center"
          style={{ top: '28%', left: '50%', transform: 'translate(-50%, -50%)' }}
        >
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center"
          >
            <div className="bg-black/50 backdrop-blur-sm rounded-full px-4 py-1.5 flex items-center gap-2">
              <Coins className="w-3.5 h-3.5 text-yellow-400" />
              <span className="text-yellow-400 text-[13px] font-bold">
                {formatCurrency(roomInfo.mainPot)}
              </span>
            </div>
            {roomInfo.sidePot > 0 && (
              <div className="bg-black/40 backdrop-blur-sm rounded-full px-3 py-0.5 mt-1">
                <span className="text-yellow-300/80 text-[10px] font-semibold">
                  Side: {formatCurrency(roomInfo.sidePot)}
                </span>
              </div>
            )}
          </motion.div>
        </div>

        {/* ========== COMMUNITY CARDS ========== */}
        <div
          className="absolute z-10 flex items-center justify-center gap-1.5"
          style={{ top: '40%', left: '50%', transform: 'translate(-50%, -50%)' }}
        >
          {communityCards.map((card, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: -20, rotateZ: -5, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, rotateZ: 0, scale: 1 }}
              transition={{ duration: 0.35, delay: i * 0.08, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <CardDisplay card={card} faceDown={!card} />
            </motion.div>
          ))}
        </div>

        {/* ========== PLAYER SEATS ========== */}
        {players.map((player, i) => (
          <PlayerSeat
            key={player.seat}
            player={player}
            position={seatPositions[i]}
            betPosition={betPositions[i]}
            color={avatarColors[i]}
            isHero={i === 0}
            onSitDown={handleSitDown}
            timerProgress={timerProgress}
          />
        ))}

        {/* ========== HERO CARDS (large, bottom) ========== */}
        {heroPlayer.cards && heroPlayer.status !== 'folded' && showHeroCards && (
          <div
            className="absolute z-30 flex items-center gap-1"
            style={{ bottom: '18%', left: '50%', transform: 'translateX(-50%)' }}
          >
            <motion.div className="flex gap-1.5" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {heroPlayer.cards.map((card, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -100, y: -50, rotateZ: -20, scale: 0.8 }}
                  animate={{ opacity: 1, x: 0, y: 0, rotateZ: i === 0 ? -3 : 3, scale: 1 }}
                  transition={{ duration: 0.5, delay: i * 0.12, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  <div
                    className={cn(
                      'w-14 h-20 rounded-xl bg-white border-2 border-gray-100 flex items-center justify-center text-lg font-black shadow-2xl',
                      (card.includes('♥') || card.includes('♦')) ? 'text-red-500' : 'text-gray-900',
                    )}
                  >
                    {card}
                  </div>
                </motion.div>
              ))}
            </motion.div>
            <button
              onClick={() => setShowHeroCards(false)}
              className="ml-1 w-6 h-6 rounded-full bg-black/40 flex items-center justify-center"
            >
              <Eye className="w-3 h-3 text-white/60" />
            </button>
          </div>
        )}

        {/* Show hero cards button when hidden */}
        {heroPlayer.cards && heroPlayer.status !== 'folded' && !showHeroCards && (
          <div
            className="absolute z-30"
            style={{ bottom: '18%', left: '50%', transform: 'translateX(-50%)' }}
          >
            <button
              onClick={() => setShowHeroCards(true)}
              className="flex items-center gap-1.5 bg-black/50 backdrop-blur-sm rounded-full px-3 py-1.5"
            >
              <Eye className="w-3.5 h-3.5 text-white/70" />
              <span className="text-white/70 text-[11px] font-medium">查看手牌</span>
            </button>
          </div>
        )}

        {/* ========== CHAT BUBBLES ========== */}
        <AnimatePresence>
          {chatMessages.map((msg) => (
            <ChatBubble
              key={msg.id}
              message={msg}
              seatPos={seatPositions[msg.seat]}
            />
          ))}
        </AnimatePresence>

        {/* ========== ACTION ZONE ========== */}
        <AnimatePresence>
          {isHeroTurn && heroPlayer.status !== 'folded' && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
              className="absolute bottom-0 left-0 right-0 z-40"
            >
              <div className="bg-gradient-to-t from-black/90 via-black/70 to-transparent pt-6 pb-3 px-3">
                {/* Raise slider row */}
                <div className="flex items-center gap-2 mb-2 px-1">
                  <span className="text-white/50 text-[10px] w-12 text-right">
                    {formatCurrency(minRaise)}
                  </span>
                  <div className="flex-1">
                    <Range
                      value={raiseAmount}
                      min={minRaise}
                      max={heroStack + (heroPlayer.bet || 0)}
                      step={1000}
                      onInput={(e: any) => setRaiseAmount(e.target.value)}
                      className="k-color-brand-green"
                    />
                  </div>
                  <span className="text-yellow-400 text-[11px] font-bold w-16 text-left">
                    {formatCurrency(raiseAmount)}
                  </span>
                </div>

                {/* Quick raise buttons */}
                <div className="flex items-center gap-1.5 mb-2.5 px-1">
                  <button
                    onClick={() => setRaiseAmount(Math.min(halfPot, heroStack))}
                    className="flex-1 bg-white/10 rounded-md py-1 text-white/80 text-[10px] font-semibold active:bg-white/20"
                  >
                    1/2 底池
                  </button>
                  <button
                    onClick={() => setRaiseAmount(Math.min(threeFourthPot, heroStack))}
                    className="flex-1 bg-white/10 rounded-md py-1 text-white/80 text-[10px] font-semibold active:bg-white/20"
                  >
                    3/4 底池
                  </button>
                  <button
                    onClick={() => setRaiseAmount(Math.min(fullPot, heroStack))}
                    className="flex-1 bg-white/10 rounded-md py-1 text-white/80 text-[10px] font-semibold active:bg-white/20"
                  >
                    底池
                  </button>
                  <button
                    onClick={handleAllIn}
                    className="flex-1 bg-[#FF3B30]/20 border border-[#FF3B30]/40 rounded-md py-1 text-[#FF3B30] text-[10px] font-bold active:bg-[#FF3B30]/30"
                  >
                    全押
                  </button>
                </div>

                {/* Main action buttons */}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleFold}
                    className="flex-1 !rounded-xl"
                    colors={{
                      fillBgIos: 'bg-[#FF3B30]',
                      fillTextIos: 'text-white',
                    }}
                    tonal
                    small
                  >
                    <span className="text-[13px] font-bold">棄牌</span>
                  </Button>

                  <Button
                    onClick={handleCall}
                    className="flex-[1.5] !rounded-xl"
                    small
                  >
                    <span className="text-[13px] font-bold">
                      {currentBet > (heroPlayer.bet || 0) ? `跟注 ${formatCurrency(currentBet)}` : '過牌'}
                    </span>
                  </Button>

                  <Button
                    onClick={handleRaise}
                    className="flex-1 !rounded-xl"
                    colors={{
                      fillBgIos: 'bg-[#34C759]',
                      fillTextIos: 'text-white',
                    }}
                    tonal
                    small
                  >
                    <span className="text-[13px] font-bold">加注</span>
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ========== WAITING MESSAGE (when not hero's turn) ========== */}
        {!isHeroTurn && heroPlayer.status === 'active' && (
          <div
            className="absolute z-30"
            style={{ bottom: '4%', left: '50%', transform: 'translateX(-50%)' }}
          >
            <div className="bg-black/40 backdrop-blur-sm rounded-full px-4 py-1.5">
              <span className="text-white/60 text-[11px] font-medium">
                等待其他玩家操作...
              </span>
            </div>
          </div>
        )}

        {/* Folded state message */}
        {heroPlayer.status === 'folded' && (
          <div
            className="absolute z-30"
            style={{ bottom: '4%', left: '50%', transform: 'translateX(-50%)' }}
          >
            <div className="bg-black/40 backdrop-blur-sm rounded-full px-4 py-1.5">
              <span className="text-white/40 text-[11px] font-medium">
                你已棄牌 - 等待下一手
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ========== MENU SHEET ========== */}
      <Sheet
        opened={showMenu}
        onBackdropClick={() => setShowMenu(false)}
        className="pb-safe"
      >
        <div className="flex flex-col">
          {/* Sheet handle */}
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-9 h-1 rounded-full bg-gray-300" />
          </div>

          <div className="px-4 pt-1 pb-2">
            <h3 className="text-[17px] font-semibold">遊戲選單</h3>
          </div>

          <List strongIos insetIos className="!my-0">
            <ListItem
              link
              chevron
              title="買入籌碼"
              media={
                <div className="w-8 h-8 rounded-lg bg-[#34C759]/15 flex items-center justify-center">
                  <Coins className="w-4 h-4 text-[#34C759]" />
                </div>
              }
              onClick={() => {
                setShowMenu(false)
                setTimeout(() => setShowBuyIn(true), 300)
              }}
            />
            <ListItem
              link
              chevron
              title="查看手牌歷史"
              media={
                <div className="w-8 h-8 rounded-lg bg-[#007AFF]/15 flex items-center justify-center">
                  <History className="w-4 h-4 text-[#007AFF]" />
                </div>
              }
              onClick={() => {
                setShowMenu(false)
                showToast('手牌歷史功能開發中')
              }}
            />
            <ListItem
              link
              chevron
              title="離開座位"
              media={
                <div className="w-8 h-8 rounded-lg bg-[#FF9500]/15 flex items-center justify-center">
                  <LogOut className="w-4 h-4 text-[#FF9500]" />
                </div>
              }
              onClick={() => {
                setShowMenu(false)
                showToast('你已離開座位')
              }}
            />
            <ListItem
              link
              chevron
              title="離開房間"
              media={
                <div className="w-8 h-8 rounded-lg bg-[#FF3B30]/15 flex items-center justify-center">
                  <DoorOpen className="w-4 h-4 text-[#FF3B30]" />
                </div>
              }
              onClick={() => {
                setShowMenu(false)
                setTimeout(() => setShowLeaveDialog(true), 300)
              }}
            />
          </List>

          <div className="px-4 py-3">
            <Button
              onClick={() => setShowMenu(false)}
              tonal
              className="!rounded-xl"
            >
              取消
            </Button>
          </div>
        </div>
      </Sheet>

      {/* ========== BUY-IN SHEET ========== */}
      <Sheet
        opened={showBuyIn}
        onBackdropClick={() => setShowBuyIn(false)}
        className="pb-safe"
      >
        <div className="flex flex-col">
          {/* Sheet handle */}
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-9 h-1 rounded-full bg-gray-300" />
          </div>

          <div className="px-4 pt-1 pb-3">
            <h3 className="text-[17px] font-semibold">買入籌碼</h3>
            <p className="text-[13px] text-[#8E8E93] mt-0.5">
              {roomInfo.blinds} • 最小 {formatCurrency(minBuyIn)} / 最大 {formatCurrency(maxBuyIn)}
            </p>
          </div>

          {/* Buy-in amount display */}
          <div className="px-4 mb-3">
            <div className="bg-[#F2F2F7] rounded-xl p-4 text-center">
              <div className="text-[32px] font-bold text-[#007AFF]">
                {formatCurrency(buyInAmount)}
              </div>
              <div className="text-[12px] text-[#8E8E93] mt-1">
                剩餘餘額: {formatCurrency(user?.balance || 0)}
              </div>
            </div>
          </div>

          {/* Range slider */}
          <div className="px-6 mb-2">
            <Range
              value={buyInAmount}
              min={minBuyIn}
              max={maxBuyIn}
              step={5000}
              onInput={(e: any) => setBuyInAmount(e.target.value)}
            />
          </div>

          {/* Min/Max labels */}
          <div className="flex items-center justify-between px-6 mb-4">
            <span className="text-[12px] text-[#8E8E93]">{formatCurrency(minBuyIn)}</span>
            <span className="text-[12px] text-[#8E8E93]">{formatCurrency(maxBuyIn)}</span>
          </div>

          {/* Quick select buttons */}
          <div className="flex items-center gap-2 px-4 mb-4">
            <button
              onClick={() => setBuyInAmount(minBuyIn)}
              className="flex-1 bg-[#F2F2F7] rounded-lg py-2 text-[13px] font-medium text-[#007AFF] active:bg-[#E5E5EA]"
            >
              最小
            </button>
            <button
              onClick={() => setBuyInAmount(Math.floor((minBuyIn + maxBuyIn) / 2))}
              className="flex-1 bg-[#F2F2F7] rounded-lg py-2 text-[13px] font-medium text-[#007AFF] active:bg-[#E5E5EA]"
            >
              中間
            </button>
            <button
              onClick={() => setBuyInAmount(maxBuyIn)}
              className="flex-1 bg-[#F2F2F7] rounded-lg py-2 text-[13px] font-medium text-[#007AFF] active:bg-[#E5E5EA]"
            >
              最大
            </button>
          </div>

          {/* Confirm button */}
          <div className="px-4 pb-4">
            <Button
              large
              onClick={handleConfirmBuyIn}
            >
              確認買入
            </Button>
          </div>
        </div>
      </Sheet>

      {/* ========== LEAVE DIALOG ========== */}
      <Dialog
        opened={showLeaveDialog}
        onBackdropClick={() => setShowLeaveDialog(false)}
        title="離開房間"
        content="你確定要離開房間嗎？你的籌碼將會自動歸還到錢包。"
        buttons={
          <>
            <DialogButton onClick={() => setShowLeaveDialog(false)}>
              取消
            </DialogButton>
            <DialogButton
              strong
              onClick={handleLeaveRoom}
              className="!text-[#FF3B30]"
            >
              確認離開
            </DialogButton>
          </>
        }
      />

      {/* ========== TOAST ========== */}
      <Toast
        opened={toastOpen}
        button={
          <Button
            rounded
            small
            inline
            onClick={() => setToastOpen(false)}
          >
            關閉
          </Button>
        }
      >
        <div className="shrink">{toastMessage}</div>
      </Toast>

      {/* Auto-close toast */}
      {toastOpen && <ToastAutoClose onClose={() => setToastOpen(false)} />}
    </Page>
  )
}

// ============================================================
// Toast Auto-Close Helper
// ============================================================

function ToastAutoClose({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 2500)
    return () => clearTimeout(timer)
  }, [onClose])
  return null
}
